<script setup>
import { computed } from 'vue';
import { frontendURL } from 'dashboard/helper/URLHelper.js';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useMapGetter } from 'dashboard/composables/store';

import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from 'next/polaris/ui/item';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';

const props = defineProps({
  id: {
    type: Number,
    default: 0,
  },
  inbox: {
    type: Object,
    default: () => ({}),
  },
  name: {
    type: String,
    default: '',
  },
  email: {
    type: String,
    default: '',
  },
  thumbnail: {
    type: String,
    default: '',
  },
  accountId: {
    type: [String, Number],
    default: '',
  },
  createdAt: {
    type: [String, Date, Number],
    default: '',
  },
  messageId: {
    type: Number,
    default: 0,
  },
  emailSubject: {
    type: String,
    default: '',
  },
});

const inboxGetter = useMapGetter('inboxes/getInbox');
const channelInbox = computed(() =>
  props.inbox?.id ? inboxGetter.value(props.inbox.id) : null
);

const navigateTo = computed(() => {
  const params = {};
  if (props.messageId) {
    params.messageId = props.messageId;
  }
  return frontendURL(
    `accounts/${props.accountId}/conversations/${props.id}`,
    params
  );
});

const createdAtTime = computed(() => {
  if (!props.createdAt) return '';
  return dynamicTime(props.createdAt);
});

const inboxName = computed(() => props.inbox?.name);
</script>

<template>
  <Item
    as-child
    size="sm"
    class="w-full cursor-pointer flex-nowrap items-start px-3 text-start text-polaris-foreground hover:bg-polaris-accent"
  >
    <!-- A search hit in the shape of the 3.1 conversation row (Item + the same metrics); the whole row is the link. -->
    <router-link :to="navigateTo">
      <ItemMedia>
        <Avatar
          :name="name || email"
          :src="thumbnail"
          :size="32"
          rounded-full
        />
      </ItemMedia>
      <ItemContent class="min-w-0 gap-0.5">
        <ItemTitle class="w-full justify-between font-normal">
          <span class="truncate" :class="{ capitalize: name }">{{
            name || email
          }}</span>
          <span
            v-if="createdAtTime"
            class="shrink-0 text-xs font-normal text-polaris-muted-foreground"
          >
            {{ createdAtTime }}
          </span>
        </ItemTitle>
        <p
          class="mb-0 flex min-w-0 items-center gap-1.5 type-polaris-small-label text-polaris-muted-foreground"
        >
          <span class="shrink-0 font-medium">{{ `#${id}` }}</span>
          <template v-if="inboxName">
            <span
              class="size-0.5 shrink-0 rounded-full bg-current"
              aria-hidden="true"
            />
            <ChannelIcon
              v-if="channelInbox?.id"
              :inbox="channelInbox"
              class="size-3.5 shrink-0"
            />
            <span class="truncate" :title="inboxName">{{ inboxName }}</span>
          </template>
        </p>
        <ItemDescription
          v-if="email || emailSubject"
          class="mb-0 line-clamp-none flex min-w-0 items-center gap-1.5 text-xs"
        >
          <span
            v-if="email"
            class="min-w-0 max-w-[60%] shrink-0 truncate"
            :title="email"
          >
            {{ email }}
          </span>
          <span
            v-if="email && emailSubject"
            class="size-0.5 shrink-0 rounded-full bg-current"
            aria-hidden="true"
          />
          <span
            v-if="emailSubject"
            class="min-w-0 truncate"
            :title="emailSubject"
          >
            {{ emailSubject }}
          </span>
        </ItemDescription>
      </ItemContent>
    </router-link>
  </Item>
</template>
